import { Link } from 'react-router-dom';
import { Instagram, Mail, MapPin, Phone } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Footer = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, null, { params: { email } });
      toast.success('Welcome to the Chaleur Collective!');
      setEmail('');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-foreground text-primary-foreground">
      {/* Newsletter Section */}
      <div className="border-b border-white/10">
        <div className="chaleur-container py-16 md:py-24">
          <div className="max-w-2xl mx-auto text-center">
            <p className="caption text-white/60 mb-4">Join the Collective</p>
            <h3 className="text-3xl md:text-4xl font-light mb-4">
              Stay Inspired
            </h3>
            <p className="text-white/70 mb-8">
              Be the first to discover new collections, artisan stories, and exclusive offers.
            </p>
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-white/10 border-white/20 text-white placeholder:text-white/50 rounded-none h-12"
              />
              <Button 
                type="submit" 
                disabled={loading}
                className="btn-chaleur-primary bg-white text-foreground hover:bg-white/90"
              >
                {loading ? 'Subscribing...' : 'Subscribe'}
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="chaleur-container py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6">
              <img 
                src="/images/brand/chaleur-logo-white.png" 
                alt="Chaleur" 
                className="h-10 w-auto object-contain brightness-0 invert"
              />
            </Link>
            <p className="text-white/60 text-sm mb-6 leading-relaxed">
              Embrace Giving, Embody Ownership.<br />
              Luxury scarves and wraps, handcrafted in India, shipped globally.
            </p>
            <div className="flex gap-4">
              <a
                href="https://www.instagram.com/chaleu_online"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/60 hover:text-white transition-colors"
                aria-label="Instagram"
                data-testid="footer-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Explore */}
          <div>
            <h4 className="caption text-white/60 mb-6">Explore</h4>
            <ul className="space-y-3">
              <li><Link to="/shop" className="text-sm text-white/80 hover:text-white transition-colors">Shop All</Link></li>
              <li><Link to="/shop/accessories" className="text-sm text-white/80 hover:text-white transition-colors">Accessories</Link></li>
              <li><Link to="/shop/limited-editions" className="text-sm text-white/80 hover:text-white transition-colors">Limited Editions</Link></li>
              <li><Link to="/gifting" className="text-sm text-white/80 hover:text-white transition-colors">Gifting</Link></li>
              <li><Link to="/journal" className="text-sm text-white/80 hover:text-white transition-colors">Journal</Link></li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="caption text-white/60 mb-6">Company</h4>
            <ul className="space-y-3">
              <li><Link to="/about" className="text-sm text-white/80 hover:text-white transition-colors">About Us</Link></li>
              <li><Link to="/the-craft" className="text-sm text-white/80 hover:text-white transition-colors">The Craft</Link></li>
              <li><Link to="/impact" className="text-sm text-white/80 hover:text-white transition-colors">Our Impact</Link></li>
              <li><Link to="/artisans" className="text-sm text-white/80 hover:text-white transition-colors">Meet the Artisans</Link></li>
              <li><Link to="/chaleur-club" className="text-sm text-white/80 hover:text-white transition-colors">Chaleur Club</Link></li>
              <li><Link to="/become-a-partner" className="text-sm text-white/80 hover:text-white transition-colors" data-testid="footer-become-partner">Become a Partner</Link></li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="caption text-white/60 mb-6">Support</h4>
            <ul className="space-y-3">
              <li><Link to="/contact" className="text-sm text-white/80 hover:text-white transition-colors">Contact Us</Link></li>
              <li><Link to="/track-order" className="text-sm text-white/80 hover:text-white transition-colors">Track Order</Link></li>
              <li><Link to="/faq" className="text-sm text-white/80 hover:text-white transition-colors">FAQs</Link></li>
              <li><Link to="/privacy-policy" className="text-sm text-white/80 hover:text-white transition-colors">Privacy Policy</Link></li>
              <li><Link to="/refund-policy" className="text-sm text-white/80 hover:text-white transition-colors">Refund Policy</Link></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="chaleur-container py-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-xs text-white/50">
            © {new Date().getFullYear()} Chaleur. All rights reserved. Crafted in India.
          </p>
          <div className="flex gap-6 text-xs text-white/50">
            <Link to="/faq" className="hover:text-white transition-colors">FAQs</Link>
            <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/refund-policy" className="hover:text-white transition-colors">Refund Policy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
